'use client'

import { useState } from 'react'
import { useSystemStatus, type ServiceStatus } from '@/hooks/use-system-status'
import { useLocale } from '@/lib/i18n/use-locale'

export function StatusBadge() {
  const { status, isLoading } = useSystemStatus({ refreshInterval: 60000 })
  const { t } = useLocale()
  const [showDetails, setShowDetails] = useState(false)

  // Status configurations
  const statusConfig = {
    operational: {
      color: 'green',
      bgClass: 'bg-green-50 dark:bg-green-900/20',
      textClass: 'text-green-700 dark:text-green-400',
      dotClass: 'bg-green-500',
      pingClass: 'bg-green-400',
      text: t('footer.statusOperational'),
    },
    degraded: {
      color: 'yellow',
      bgClass: 'bg-yellow-50 dark:bg-yellow-900/20',
      textClass: 'text-yellow-700 dark:text-yellow-400',
      dotClass: 'bg-yellow-500',
      pingClass: 'bg-yellow-400',
      text: t('footer.statusDegraded'),
    },
    down: {
      color: 'red',
      bgClass: 'bg-red-50 dark:bg-red-900/20',
      textClass: 'text-red-700 dark:text-red-400',
      dotClass: 'bg-red-500',
      pingClass: 'bg-red-400',
      text: t('footer.statusDown'),
    },
    checking: {
      color: 'gray',
      bgClass: 'bg-gray-50 dark:bg-gray-800',
      textClass: 'text-gray-700 dark:text-gray-400',
      dotClass: 'bg-gray-500',
      pingClass: 'bg-gray-400',
      text: t('footer.statusChecking'),
    },
  }

  const config = statusConfig[status.overall]

  const formatResponseTime = (time?: number) => {
    if (!time) return ''
    if (time < 1000) return `${time}ms`
    return `${(time / 1000).toFixed(2)}s`
  }

  const getServiceIcon = (service: ServiceStatus) => {
    switch (service.status) {
      case 'operational':
        return '✓'
      case 'degraded':
        return '⚠'
      case 'down':
        return '✗'
    }
  }

  return (
    <div className="relative">
      {/* Status Badge */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-3 py-1 ${config.bgClass} ${config.textClass} rounded-full text-xs font-medium transition-all hover:scale-105 cursor-pointer`}
        title={t('footer.statusTooltip')}
      >
        <span className="relative flex h-2 w-2">
          {status.overall === 'operational' && !isLoading && (
            <span
              className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pingClass} opacity-75`}
            ></span>
          )}
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${config.dotClass}`}
          ></span>
        </span>
        {config.text}
      </button>

      {/* Dropdown Details */}
      {showDetails && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDetails(false)}
          />

          {/* Details Card */}
          <div className="absolute bottom-full right-0 mb-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-50">
            <div className="space-y-2">
              {/* Header */}
              <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                  {t('footer.systemStatus')}
                </h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(status.timestamp).toLocaleTimeString()}
                </span>
              </div>

              {/* Services */}
              <div className="space-y-1.5">
                {status.services.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          service.status === 'operational'
                            ? 'text-green-600 dark:text-green-400'
                            : service.status === 'degraded'
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400'
                        }
                      >
                        {getServiceIcon(service)}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {service.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {service.responseTime && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatResponseTime(service.responseTime)}
                        </span>
                      )}
                      {service.message && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {service.message}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('footer.refreshEvery')} 60s
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
